import {
  createContext,
  useContext,
  useMemo,
  useState,
  ReactNode,
  SetStateAction,
  Dispatch,
} from 'react';
import * as diff from 'diff';

interface DiffMergeContextType {
  original: string;
  setOriginal: Dispatch<SetStateAction<string>>;
  edited: string;
  setEdited: (value: string) => void;
  diffParts: diff.Change[];
  hasChanges: () => boolean;
  showDiff: boolean;
  setShowDiff: React.Dispatch<React.SetStateAction<boolean>>;
}

const DiffMergeContext = createContext<DiffMergeContextType | undefined>(
  undefined
);

export const DiffMergeProvider = ({ children }: { children: ReactNode }) => {
  const [original, setOriginal] = useState(''); // Default empty, set by OriginalView
  const [edited, setEdited] = useState(''); // Default empty, set by EditedView
  const [showDiff, setShowDiff] = useState<boolean>(false);
  const diffParts = useMemo(
    () => diff.diffWords(original, edited),
    [original, edited]
  );

  const hasChanges = () => original !== edited;

  return (
    <DiffMergeContext.Provider
      value={{
        original,
        edited,
        setEdited,
        setOriginal,
        diffParts,
        hasChanges,
        showDiff,
        setShowDiff,
      }}
    >
      {children}
    </DiffMergeContext.Provider>
  );
};

export const useDiffMerge = () => {
  const context = useContext(DiffMergeContext);
  if (!context) {
    throw new Error('useDiffMerge must be used within a DiffMergeProvider');
  }
  return context;
};
